import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import React from "react";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { Checkbox, CheckboxField, CheckboxGroup } from "src/components/base/checkbox";
import { ApiInternal } from "src/api/api_internal";
import { toast } from "react-toastify";

/**
 * The properties for {@link AddDomainDialog}
 */
export type AddDomainDialogProps = {
    /** Callback for closing the dialog */
    onClose: () => void;
    /** Callback for submitting the form */
    onSubmit: () => void;
};

/**
 * A dialog for adding a new split dns domain
 */
export default function AddDomainDialog(props: AddDomainDialogProps) {
    const [t] = useTranslation("dialog-add-domain");

    const form = useForm({
        defaultValues: {
            domainName: "",
            disabled: false,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiInternal.dns.addDomainSplitDns({
                    domain: value.domainName,
                    disabled: value.disabled,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            domainName: res.error.domain_invalid
                                ? t("error.invalid-domain")
                                : res.error.domain_already_exists
                                  ? t("error.domain-already-exists")
                                  : undefined,
                        },
                    };
                }
            },
        },
        onSubmit: () => {
            props.onSubmit();
            toast.success(t("toast.saved-successfully"));
        },
    });

    return (
        <Dialog onClose={props.onClose} open={true}>
            <Form onSubmit={form.handleSubmit}>
                <DialogTitle>{t("heading.add-domain")}</DialogTitle>
                <DialogBody>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"domainName"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.name")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            max={255}
                                            required={true}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage key={err}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <form.Field name={"disabled"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.disabled")}</Label>
                                        <CheckboxGroup>
                                            <CheckboxField>
                                                <Checkbox
                                                    checked={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e)}
                                                />
                                                <Label>{t("description.disabled")}</Label>
                                            </CheckboxField>
                                        </CheckboxGroup>
                                    </Field>
                                )}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>
                </DialogBody>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("button.cancel")}</Button>
                    <PrimaryButton type={"submit"}>{t("button.add")}</PrimaryButton>
                </DialogActions>
            </Form>
        </Dialog>
    );
}
